<template>
  <nav class="nav-bar" :key="forceRender">
    <div class="nav-container">
      <!-- Logo -->
      <div class="nav-left">
        <div class="nav-logo">
          <router-link :to="logo.link_url || '/'">
            <img :src="logo.image_url || '/logo.png'" :alt="logo.alt_text" class="logo-image">
          </router-link>
        </div>
      </div>

      <!-- 主导航菜单 -->
      <div class="nav-menu">
        <router-link to="/" class="nav-link">首页</router-link>
        
        <!-- 素材中心 -->
        <div class="nav-dropdown">
          <span class="nav-link">素材中心</span>
          <div class="dropdown-content">
            <router-link to="/materials/character">人物素材</router-link>
            <router-link to="/materials/scene">场景素材</router-link>
            <router-link to="/materials/expression">表情素材</router-link>
            <router-link to="/materials/animal">兽类素材</router-link>
            <router-link to="/materials/prop">道具素材</router-link>
            <router-link to="/materials/effect">特效素材</router-link>
            <router-link to="/materials/ai">AI素材</router-link>
          </div>
        </div>

        <router-link to="/custom" class="nav-link">定制中心</router-link>

        <!-- 配音中心 -->
        <div class="nav-dropdown">
          <span class="nav-link">配音中心</span>
          <div class="dropdown-content">
            <a href="https://www.moyin.com/" target="_blank">魔音工坊</a>
            <a href="https://www.douge.com/companyProfile" target="_blank">逗哥配音</a>
            <a href="https://www.ttson.cn/?source=BiC2Ho" target="_blank">海豚配音</a>
            <a href="https://www.modelscope.cn/studios/iic/CosyVoice2-0.5B" target="_blank">CosyVoice</a>
            <a href="https://ttsmaker.cn/" target="_blank">免费配音</a>
          </div>
        </div>

        <router-link to="/tools" class="nav-link">实用工具</router-link>

        <!-- 视频发布 -->
        <div class="nav-dropdown">
          <span class="nav-link">视频发布</span>
          <div class="dropdown-content">
            <a href="https://creator.douyin.com" target="_blank">抖音</a>
            <a href="https://cp.kuaishou.com" target="_blank">快手</a>
            <a href="https://member.bilibili.com/platform/home" target="_blank">B站</a>
            <a href="https://channels.weixin.qq.com" target="_blank">微信视频号</a>
            <a href="https://baijiahao.baidu.com" target="_blank">百家号</a>
            <a href="https://creator.xiaohongshu.com" target="_blank">小红书</a>
            <a href="https://qqzz.qq.com/public/login" target="_blank">QQ小视频</a>
            <a href="https://mp.v.qq.com/publishVideo/video" target="_blank">腾讯视频</a>
            <a href="https://c.alipay.com/page/portal/home" target="_blank">支付宝</a>
          </div>
        </div>

        <router-link to="/monetize" class="nav-link">变现渠道</router-link>
        <router-link to="/ai-tool" class="nav-link">简笔奇迹AI</router-link>
        <router-link to="/tutorial" class="nav-link">教程中心</router-link>
      </div>

      <!-- 右侧功能区 -->
      <div class="nav-right">
        <!-- 直接使用NotificationCenter组件 -->
        <NotificationCenter />

        <!-- 用户头像区域 - 已登录状态 -->
        <div 
          class="user-wrapper" 
          v-if="isAuthenticated"
          @mouseenter="showUserMenu = true"
          @mouseleave="showUserMenu = false"
        >
          <div class="username-display">
            {{ getUserDisplayName() }}
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </div>
          <!-- 用户菜单 -->
          <div class="user-menu" v-show="showUserMenu">
            <a @click="showChangePasswordDialog = true">修改密码</a>
            <a href="https://docs.qq.com/form/page/DZXBaRGtNSE1SRnN6" target="_blank">账号报备</a>
            <a @click="handleLogout">退出登录</a>
          </div>
        </div>
        
        <!-- 未登录状态 -->
        <div v-else class="login-wrapper">
          <a href="javascript:void(0)" class="login-btn" @click="showLoginDialog">登录</a>
        </div>
      </div>
    </div>
  </nav>

  <!-- 添加修改密码弹窗 -->
  <ChangePasswordDialog
    v-model:visible="showChangePasswordDialog"
    @success="handlePasswordChangeSuccess"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick, inject, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import NotificationCenter from '@/components/notification/NotificationCenter.vue'
import ChangePasswordDialog from '@/components/user/ChangePasswordDialog.vue'
import { Bell, ArrowDown } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
// 引入自定义认证状态管理
import authState from '@/utils/authState'
// 引入登录弹窗管理
import authDialog from '@/utils/authDialog'

// 定义接收的 props
const props = defineProps({
  isLoggedIn: {
    type: Boolean,
    default: false
  }
});

const router = useRouter()
const userStore = useUserStore()
const showUserMenu = ref(false)
const forceRender = ref(Date.now()) // 用于强制组件重新渲染的key值
let prevAuthState = false // 记录上一次的认证状态

// 使用计算属性来监听登录状态
const isAuthenticated = computed(() => {
  // 使用authState的isLoggedIn计算属性
  const isLoggedIn = authState.isLoggedIn.value;
  console.log('NavBar - 使用authState检查登录状态:', isLoggedIn);
  
  // 如果当前渲染周期检测到登录状态变化，设置forceRender为当前时间戳
  if (prevAuthState !== isLoggedIn) {
    console.log(`NavBar - 检测到登录状态变化: ${prevAuthState} -> ${isLoggedIn}, 将在下一周期强制更新UI`);
    prevAuthState = isLoggedIn;
    
    // 使用setTimeout确保在当前渲染周期后更新
    setTimeout(() => {
      forceRender.value = Date.now();
      console.log('NavBar - 强制更新UI触发, key:', forceRender.value);
    }, 0);
  }
  
  return isLoggedIn;
});

// 监视用户状态变化，主动刷新状态
watch(() => userStore.isLoggedIn, (newValue, oldValue) => {
  console.log('NavBar - 用户登录状态变化:', oldValue, '->', newValue);
  // 强制组件重新计算状态
  nextTick(() => {
    console.log('NavBar - 用户状态刷新后:', { 
      storeLoggedIn: userStore.isLoggedIn,
      localStorage: !!localStorage.getItem('token')
    });
  });
}, { immediate: true });

// 监视 props 变化
watch(() => props.isLoggedIn, (newValue) => {
  console.log('NavBar - props.isLoggedIn 变化:', newValue);
  // 强制检查登录状态
  nextTick(() => {
    console.log('NavBar - props变化后检查:', { 
      storeLoggedIn: userStore.isLoggedIn,
      propsLoggedIn: props.isLoggedIn
    });
  });
}, { immediate: true });

// Logo数据
const logo = ref({
  image_url: '',
  link_url: '/',
  alt_text: '简笔奇迹素材网'
})

// 获取Logo数据
const fetchLogo = async () => {
  try {
    const response = await fetch('/api/logo')
    if (response.ok) {
      const data = await response.json()
      if (data) {
        logo.value = data
      }
    }
  } catch (error) {
    console.error('获取Logo失败:', error)
  }
}

// 使用ref记录最后更新的时间戳，用于强制更新
const lastUpdateTimestamp = ref(Date.now());

// 添加一个轮询函数，定期检查localStorage中的登录状态
let authCheckInterval: ReturnType<typeof setInterval> | null = null;

// 在组件挂载时设置轮询检查
onMounted(() => {
  fetchLogo();
  
  // 添加全局事件监听
  window.addEventListener('auth:login', handleAuthChange);
  window.addEventListener('auth:logout', handleAuthChange);
  
  console.log('NavBar组件挂载 - 登录状态检查:', {
    authState: authState.isLoggedIn.value,
    username: authState.user.value?.username || null
  });
});

// 组件卸载时移除监听器
onUnmounted(() => {
  // 移除全局事件监听
  window.removeEventListener('auth:login', handleAuthChange);
  window.removeEventListener('auth:logout', handleAuthChange);
  
  // 清除轮询定时器
  if (authCheckInterval) {
    clearInterval(authCheckInterval);
  }
});

// 定义事件处理器类型
interface AuthEvent extends Event {
  type: string;
  detail?: any;
}

// 处理认证状态变化
const handleAuthChange = (event: AuthEvent) => {
  console.log('NavBar - 收到认证状态变化事件:', event.type);
  // 强制重新渲染
  forceRender.value = Date.now();
};

// 处理退出登录
const handleLogout = async () => {
  try {
    console.log('开始登出操作')
    
    // 使用authState登出
    authState.logout();
    
    // 隐藏用户菜单
    showUserMenu.value = false
    
    // 强制刷新UI状态
    await nextTick()
    forceRender.value = Date.now()
    console.log('NavBar - 登出后强制更新UI，key:', forceRender.value)
    
    // 显示成功消息
    ElMessage.success('退出登录成功')
    
    // 留在当前页面，或导航到首页
    if (router.currentRoute.value.meta.requiresAuth) {
      router.push('/')
    }
  } catch (error) {
    console.error('退出登录失败:', error)
    ElMessage.error('退出登录失败，请重试')
    
    // 即使出错也强制清除状态
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    
    // 强制刷新UI
    await nextTick()
    forceRender.value = Date.now()
    console.log('NavBar - 登出错误后强制更新UI，key:', forceRender.value)
    
    // 导航到首页
    router.push('/')
  }
}

// 格式化用户名，中间使用*代替
const formatUsername = (username?: string, showFull: boolean = false) => {
  if (!username) return '用户';
  
  // 如果选择显示完整用户名，直接返回
  if (showFull) return username;
  
  // 判断是否是11位数字（手机号）
  if (/^\d{11}$/.test(username)) {
    // 显示前3位和后4位，中间4位用*代替
    return username.substring(0, 3) + '****' + username.substring(7);
  }
  
  // 其他情况，保留首尾字符，中间用*代替
  const length = username.length;
  if (length <= 2) return username;
  
  const firstChar = username.charAt(0);
  const lastChar = username.charAt(length - 1);
  // 根据长度决定中间*的数量，最少1个
  const maskLength = Math.min(length - 2, 4);
  const mask = '*'.repeat(maskLength);
  
  return firstChar + mask + lastChar;
}

// 修改密码相关
const showChangePasswordDialog = ref(false);

// 处理密码修改成功
const handlePasswordChangeSuccess = () => {
  showChangePasswordDialog.value = false;
  showUserMenu.value = false;
  ElMessage.success('密码修改成功');
};

// 强制导航到登录页面
const forceNavigateToLogin = (event: Event) => {
  event.preventDefault();
  // 显示登录弹窗
  showLoginDialog();
};

// 显示登录弹窗
const showLoginDialog = () => {
  authDialog.show();
};

// 获取用户显示名称
const getUserDisplayName = () => {
  // 从authState获取用户名
  if (authState.user.value?.username) {
    console.log('NavBar - 从authState获取用户名:', authState.user.value.username);
    return formatUsername(authState.user.value.username, false); // 使用掩码处理，不显示完整用户名
  }
  
  // 默认显示
  return '用户';
};
</script>

<style scoped>
.nav-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: white;
  box-shadow: none;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

.nav-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  padding: 0;
  box-sizing: border-box;
}

.nav-left {
  display: flex;
  align-items: center;
  padding-left: 0;
  margin-left: 0;
  position: absolute;
  left: 0;
}

.nav-logo {
  margin-right: 40px;
  flex-shrink: 0;
}

.logo-image {
  height: 40px;
}

.nav-menu {
  flex: 1;
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  overflow: visible;
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 60px; /* 添加左右padding，为两侧的绝对定位元素留出空间 */
}

.nav-link {
  color: #333;
  text-decoration: none;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.3s;
  white-space: nowrap;
  height: 100%;
  display: flex;
  align-items: center;
}

.nav-link:hover {
  color: var(--primary-blue);
  background: #f5f7fa;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  padding-right: 0;
  margin-right: 0;
  position: absolute;
  right: 10px;
}

.login-wrapper {
  display: flex;
  align-items: center;
}

.login-btn {
  padding: 6px 20px;
  background-color: #409EFF;
  color: white;
  border-radius: 16px;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s;
  border: none;
  cursor: pointer;
}

.login-btn:hover {
  background-color: #66b1ff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.nav-dropdown {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0;
}

.nav-dropdown .nav-link {
  cursor: pointer;
  padding: 8px 12px;
  color: #333;
  text-decoration: none;
  font-size: 16px;
  border-radius: 4px;
  transition: all 0.3s;
}

.nav-dropdown .nav-link:hover {
  color: var(--primary-blue);
  background: #f5f7fa;
}

.dropdown-content {
  display: none;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  min-width: 160px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  border-radius: 4px;
  padding: 8px 0;
  z-index: 1001;
  margin-top: 1px;
}

.nav-dropdown:hover {
  background: #f5f7fa;
}

.nav-dropdown:hover .dropdown-content {
  display: block;
}

.dropdown-content a {
  display: block;
  padding: 8px 16px;
  color: #333;
  text-decoration: none;
  transition: all 0.3s;
  text-align: center;
  white-space: nowrap;
}

.dropdown-content a:hover {
  background: #f5f7fa;
  color: #1890ff;
}

.dropdown-content::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid white;
}

.notification-wrapper {
  position: relative;
  margin-right: 20px;
}

.notification-icon {
  position: relative;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  color: #666;
}

.notification-icon:hover {
  color: #1890ff;
}

.unread-badge {
  position: absolute;
  top: 0;
  right: 0;
  background: #ff4d4f;
  color: white;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 12px;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
}

.notification-preview {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  min-width: 200px;
  max-width: 300px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  border-radius: 4px;
  padding: 8px 0;
  z-index: 1000;
}

.notification-item {
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.3s;
}

.notification-item:hover {
  background: #f5f7fa;
}

.no-notification {
  padding: 16px;
  text-align: center;
  color: #999;
}

.user-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  object-fit: cover;
  border: 2px solid #eee;
}

.username-display {
  padding: 6px 12px;
  background-color: #f0f5ff;
  border-radius: 16px;
  color: #409EFF;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid #d9ecff;
  transition: all 0.3s;
}

.username-display:hover {
  background-color: #ecf5ff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-menu {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  min-width: 120px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  border-radius: 4px;
  padding: 8px 0;
  z-index: 1001;
  margin-top: 8px;
}

.user-menu::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid white;
}

.user-menu a {
  display: block;
  padding: 8px 16px;
  color: #333;
  text-decoration: none;
  transition: all 0.3s;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
}

.user-menu a:hover {
  background: #f5f7fa;
  color: #1890ff;
}
</style> 